
import { useState } from "react";
import { ProductForm } from "./ProductForm";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { ProductCard } from "./products/ProductCard";
import { ProductLoadingSkeleton } from "./products/ProductLoadingSkeleton";
import { EmptyProductList } from "./products/EmptyProductList";
import { ProductDeleteDialog } from "./products/ProductDeleteDialog";
import { Product } from "@/components/services/products/types";

interface ProductListProps {
  products: Product[];
  isLoading: boolean;
}

export const ProductList = ({ products, isLoading }: ProductListProps) => {
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleDelete = async () => {
    if (!deletingProduct) return;

    try {
      const { error } = await supabase
        .from("professional_products")
        .delete()
        .eq("id", deletingProduct.id);

      if (error) throw error;

      toast({
        title: "Produit supprimé",
        description: "Le produit a été supprimé avec succès",
      });
      
      queryClient.invalidateQueries({ queryKey: ["professional-products"] });
    } catch (error) {
      console.error("Error deleting product:", error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer le produit",
      });
    } finally {
      setDeletingProduct(null);
    }
  };

  if (isLoading) {
    return <ProductLoadingSkeleton />;
  }

  if (products.length === 0) {
    return <EmptyProductList />;
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            onEdit={setEditingProduct}
            onDelete={setDeletingProduct}
          />
        ))}
      </div>

      {editingProduct && (
        <ProductForm
          open={!!editingProduct}
          onOpenChange={() => setEditingProduct(null)}
          product={{
            id: editingProduct.id,
            title: editingProduct.title,
            description: editingProduct.description,
            price: editingProduct.price,
            condition: editingProduct.condition,
            address: editingProduct.address,
            phone: editingProduct.phone,
            photo_url: editingProduct.photo_url,
            quotes: editingProduct.quotes,
            currency: editingProduct.currency || "EUR"
          }}
        />
      )}

      <ProductDeleteDialog 
        open={!!deletingProduct}
        onOpenChange={() => setDeletingProduct(null)}
        onConfirm={handleDelete}
      />
    </>
  );
};
